import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { styles, colors } from '../styles/styles';
import { formatTimestamp } from '../utils/dataProcessing';
import { useSwipeable } from 'react-swipeable';
import { 
    getCollectionsForThought, 
    getCollections, 
    addThoughtToCollectionsBatch, 
    removeThoughtFromCollectionsBatch 
} from '../services/collectionService';
import { suggestTagsForThought, suggestNewThemes, hybridTagsEnabled } from '../services/openAiService';
import { getThoughtById } from '../services/thoughtService';
import { addToDeck, tossCard } from '../services/deckService';
import { cachedSuggestTags, abortTagSuggestions, tagSuggestionMode } from '../utils/aiSuggestionsCache';
import { isPrefetchableText } from '../utils/draftPredicate';
import { getAllUniqueTags } from '../utils/tagUtils';
import PlusIcon from './common/PlusIcon';
import ActionButton from './common/ActionButton';
import Identicon from './common/Identicon';
import TagsInput from './TagsInput';
import useTags from '../hooks/useTags';
import CreateCollectionModal from './CreateCollectionModal';

// In existingTags mode, hybrid returns a fused list that intentionally includes
// fresh LLM tags not yet in your vocabulary — keep them. Pure KNN only ever
// returns existing tags, so filtering to vocab is a no-op there. Takes the
// hybrid snapshot as an arg so the request mode and this filter can't disagree.
const applyExistingTagsFilter = (suggestions, allTags, hybrid) =>
  hybrid ? suggestions : suggestions.filter(tag => allTags.includes(tag));

const ThoughtDetail = ({ thoughts, allTags: allTagsProp, onSave, updateThoughtLocally, isDataLoading = false, deleteThought, thoughtId, cardMode = false }) => {
    const params = useParams();
    const id = thoughtId || params.id;
    const navigate = useNavigate();
    const [thought, setThought] = useState(null);
    // Mirrors the latest committed `thought` so async handlers (e.g. the deck
    // toggle's failure revert) can merge onto current state instead of a stale
    // click-time snapshot.
    const latestThoughtRef = useRef(null);
    
    // Add state for copy button icon
    const [showCopyCheck, setShowCopyCheck] = useState(false);
    const [showCommentCopyCheck, setShowCommentCopyCheck] = useState(false);
    
    // Keep a ref to the current UI value to prevent re-renders
    const importanceValueRef = useRef(null);
    // Tracks the id we've already resolved (from props or by-id fetch) so the
    // load effect doesn't re-fetch a triaged thought on every `thoughts` change.
    const loadedIdRef = useRef(null);
    const [isSaving, setIsSaving] = useState(false);
    const [saveTimeout, setSaveTimeout] = useState(null);
    // State for comments editing
    const [showCommentsEditor, setShowCommentsEditor] = useState(false);
    const [commentsValue, setCommentsValue] = useState('');
    const [isCommentsSaving, setIsCommentsSaving] = useState(false);
    
    // Use both isDataLoading from props and local loading state
    const [isLocalLoading, setIsLocalLoading] = useState(true);
    const isLoading = isDataLoading || isLocalLoading;
    // Fade in animation state
    const [isVisible, setIsVisible] = useState(false);

    // State for collections this thought appears in
    const [collections, setCollections] = useState([]);
    const [collectionsLoading, setCollectionsLoading] = useState(true);
    
    // New state for inline collections management
    const [showCollectionsManager, setShowCollectionsManager] = useState(false);
    const [allCollections, setAllCollections] = useState([]);
    const [selectedCollectionIds, setSelectedCollectionIds] = useState([]);
    const [isCollectionsSaving, setIsCollectionsSaving] = useState(false);
    const [collectionSearchTerm, setCollectionSearchTerm] = useState('');
    const [showCreateCollectionModal, setShowCreateCollectionModal] = useState(false);
    
    // State for suggested tags from OpenAI
    const [suggestedTags, setSuggestedTags] = useState([]);
    const [suggestingTags, setSuggestingTags] = useState(false);
    
    // State for selected AI suggested tags
    const [selectedAiTags, setSelectedAiTags] = useState([]);
    const [isSavingTags, setIsSavingTags] = useState(false);
    const [hideAiSuggestions, setHideAiSuggestions] = useState(false);
    const [permanentlyHideAiSuggestions, setPermanentlyHideAiSuggestions] = useState(false);
    
    // State to track which suggestion mode we're in (regular tags or new themes)
    const [suggestionMode, setSuggestionMode] = useState('existingTags'); // 'existingTags' or 'newThemes'

    // Add state for showing tags editor
    const [showTagsEditor, setShowTagsEditor] = useState(false);

    // Initialize tags manager
    const tagsManager = useTags();

    // Update tags manager when thought changes
    // Keep latestThoughtRef in lockstep with the committed thought so async
    // handlers can merge onto current state rather than a stale snapshot.
    useEffect(() => { latestThoughtRef.current = thought; }, [thought]);

    useEffect(() => {
        if (thought?.tags) {
            tagsManager.setTags(thought.tags);
        } else {
            tagsManager.resetTags();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [thought]); // Only depend on thought changes

    // Update available tags when thoughts change. Prefer the memoized allTags
    // prop when the parent supplies it (review mode); fall back to scanning
    // locally for the legacy route-based render.
    useEffect(() => {
        const allTags = allTagsProp || getAllUniqueTags(thoughts);
        tagsManager.updateAvailableTags(allTags);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [thoughts, allTagsProp]);

    // Save tags when they change
    const handleTagsChange = async (newTags) => {
        if (!thought) return;
        
        try {
            const updatedThought = { ...thought, tags: newTags };
            
            // If updateThoughtLocally is available, use it for optimistic updates
            if (updateThoughtLocally) {
                // Optimistically update the local state first
                updateThoughtLocally(updatedThought);
                
                // Save without triggering a full data reload
                await onSave(updatedThought, true);
            } else {
                // Fallback to regular save that might reload all data
                await onSave(updatedThought);
            }
            
            // Update the local thought object
            setThought(prev => ({
                ...prev,
                tags: newTags
            }));
        } catch (error) {
            console.error('Error saving tags:', error);
        }
    };

    // Create a function for back navigation to use in both button and swipe.
    // No-op in cardMode: the chrome that triggers this is hidden, but the
    // bound swipe handler can still fire on edge cases — never navigate
    // away from the parent surface (e.g. DraftsReview) without its consent.
    const handleBack = () => {
        if (cardMode) return;
        // Apply exit animation before navigation
        const detailContainer = document.querySelector('.page-content');
        if (detailContainer) {
            detailContainer.style.animation = 'fadeOut 0.2s cubic-bezier(0.2, 0, 0, 1) forwards';
            setTimeout(() => {
                navigate('/', { replace: true });
            }, 180);
        } else {
            navigate('/', { replace: true });
        }
    };

    // Swipe handlers for going back (no-op in cardMode; the review surface handles swipes there)
    const swipeHandlers = useSwipeable({
        onSwipedRight: (eventData) => {
            if (cardMode) return;
            if (eventData.initial[0] < 50) {
                handleBack();
            }
        },
        trackMouse: false,
        delta: 50,
        preventDefaultTouchmoveEvent: false,
    });

    // Update the thought when thoughts array changes or id changes.
    //
    // The home page now loads only DRAFT thoughts, so a triaged thought opened
    // directly by URL won't be in the `thoughts` prop. When that happens we
    // fall back to fetching it by id from Supabase. loadedIdRef guards against
    // re-fetching on every unrelated `thoughts` change once we already have it.
    useEffect(() => {
        let cancelled = false;
        const applyThought = (t) => {
            setThought(t);
            if (t) {
                importanceValueRef.current = t.importance !== undefined ? t.importance : null;
                updateSliderUI(importanceValueRef.current);
            }
        };

        const local = thoughts.find(t => t.id === id);
        if (local) {
            loadedIdRef.current = id;
            applyThought(local);
            return;
        }

        // Not in the (drafts-only) array. Fetch by id once per id. The guard is
        // `loadedIdRef.current !== id`, and we set the ref on EVERY terminal
        // path (success, not-found, error) — otherwise a not-found/errored
        // fetch would re-fire on every unrelated `thoughts` change (infinite
        // round-trips). Navigation to a different id still refetches because
        // the ref then holds the previous id.
        if (id && loadedIdRef.current !== id) {
            getThoughtById(id)
                .then(fetched => {
                    if (cancelled) return;
                    loadedIdRef.current = id;
                    applyThought(fetched || null); // null clears stale content for a deleted id
                })
                .catch(() => {
                    if (cancelled) return;
                    loadedIdRef.current = id; // stop retrying on a transient error; "not found" UI handles it
                });
        }

        return () => { cancelled = true; };
    }, [thoughts, id]);

    // Clean up timeout on unmount
    useEffect(() => {
        return () => {
            if (saveTimeout) clearTimeout(saveTimeout);
        };
    }, [saveTimeout]);

    // Set loading to false after a short delay to ensure data is ready
    useEffect(() => {
        const timer = setTimeout(() => {
            setIsLocalLoading(false);
        }, 100); // Updated to match FieldEditPage's 100ms delay
        return () => clearTimeout(timer);
    }, []);

    // Trigger fade in animation after component mounts and loading is done
    useEffect(() => {
        if (!isLoading && thought) {
            // Small delay to ensure DOM is ready
            const timer = setTimeout(() => {
                setIsVisible(true);
            }, 10); // Reduced delay to match FieldEditPage
            return () => clearTimeout(timer);
        }
    }, [isLoading, thought]);

    // Get collections this thought belongs to
    const fetchCollections = async () => {
        if (thought) {
            setCollectionsLoading(true);
            try {
                const thoughtCollections = await getCollectionsForThought(thought.id);
                setCollections(thoughtCollections);
            } catch (error) {
                console.error('Error fetching collections:', error);
                setCollections([]);
            } finally {
                setCollectionsLoading(false);
            }
        }
    };

    // Add useEffect to fetch collections when thought changes
    useEffect(() => {
        fetchCollections();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [thought?.id]); // Only re-fetch when thought ID changes

    // Function to fetch all available collections
    const fetchAllCollections = async () => {
        try {
            const allCollections = await getCollections();
            // Filter out tag collections and default collection
            const filteredCollections = allCollections.filter(collection => 
                !collection.isTagCollection && !collection.isDefault
            );
            setAllCollections(filteredCollections);
            // Set initially selected collections
            setSelectedCollectionIds(collections.map(c => c.id));
        } catch (error) {
            console.error('Error fetching all collections:', error);
            setAllCollections([]);
        }
    };

    // Function to toggle collection selection
    const toggleCollectionSelection = (collectionId) => {
        setSelectedCollectionIds(prev => {
            if (prev.includes(collectionId)) {
                return prev.filter(id => id !== collectionId);
            } else {
                return [...prev, collectionId];
            }
        });
    };

    // Function to handle saving collection changes
    const handleSaveCollections = async () => {
        if (isCollectionsSaving) return;
        
        setIsCollectionsSaving(true);
        try {
            // Get existing collection IDs
            const existingIds = collections.map(c => c.id);
            
            // Handle adds and removes
            const idsToAdd = selectedCollectionIds.filter(id => !existingIds.includes(id));
            const idsToRemove = existingIds.filter(id => !selectedCollectionIds.includes(id));
            
            if (idsToAdd.length > 0) {
                await addThoughtToCollectionsBatch(thought.id, idsToAdd);
            }
            
            if (idsToRemove.length > 0) {
                await removeThoughtFromCollectionsBatch(thought.id, idsToRemove);
            }
            
            // Refresh collections
            await fetchCollections();
            
            // Close the manager
            setShowCollectionsManager(false);
        } catch (error) {
            console.error('Error saving collections:', error);
        } finally {
            setIsCollectionsSaving(false);
        }
    };

    // Function to handle collection creation success
    const handleCollectionCreated = async (collectionName) => {
        setShowCreateCollectionModal(false);
        // Wait for collections to be fetched
        await fetchAllCollections();
        // After fetching, select the newly created collection
        const newCollection = allCollections.find(c => c.name === collectionName);
        if (newCollection) {
            setSelectedCollectionIds(prev => [...prev, newCollection.id]);
        }
    };

    // Use a ref for thoughts so the suggestion effect can read the latest value
    // without re-firing every time the thoughts array gets a new reference
    const thoughtsRef = useRef(thoughts);
    useEffect(() => { thoughtsRef.current = thoughts; }, [thoughts]);

    // Get tag suggestions from OpenAI when thought is loaded
    useEffect(() => {
        let cancelled = false;
        const thoughtText = thought?.thought;

        const getSuggestedTags = async () => {
            if (permanentlyHideAiSuggestions) return;

            if (thought && thoughtText) {
                if (!isPrefetchableText(thoughtText)) {
                    setSuggestedTags([]);
                    return;
                }

                setSuggestingTags(true);
                try {
                    const allTags = allTagsProp || getAllUniqueTags(thoughtsRef.current);
                    const existingTags = thought.tags || [];
                    // One hybrid snapshot drives the request, the cache key, and
                    // the filter below — so they can never disagree mid-fetch.
                    const hybrid = hybridTagsEnabled();

                    let suggestions = await cachedSuggestTags(
                        thought.id,
                        tagSuggestionMode(suggestionMode, hybrid),
                        existingTags,
                        (signal) =>
                            suggestionMode === 'existingTags'
                                ? suggestTagsForThought(thoughtText, allTags, existingTags, { signal, hybrid })
                                : suggestNewThemes(thoughtText, allTags, existingTags, { signal })
                    );
                    if (suggestionMode === 'existingTags') {
                        suggestions = applyExistingTagsFilter(suggestions, allTags, hybrid);
                    } else {
                        suggestions = suggestions.filter(tag => !allTags.includes(tag));
                    }

                    if (!cancelled) setSuggestedTags(suggestions);
                } catch (error) {
                    if (!cancelled && error?.name !== 'AbortError') {
                        console.error('Error getting tag suggestions:', error);
                    }
                } finally {
                    if (!cancelled) setSuggestingTags(false);
                }
            }
        };

        getSuggestedTags();
        return () => { cancelled = true; };
    // thoughts intentionally excluded — accessed via ref to avoid re-firing
    // when the array reference changes but the data hasn't
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [thought?.id, thought?.thought, suggestionMode, permanentlyHideAiSuggestions]);

    // Function to manually refresh AI suggestions
    const refreshAiSuggestions = () => {
        // Skip if suggestions are permanently hidden
        if (permanentlyHideAiSuggestions) {
            return;
        }

        // One hybrid snapshot drives the cache-bust, the request, and the filter.
        const hybrid = hybridTagsEnabled();

        // Bust the cache for this id+mode so the user actually gets fresh suggestions
        if (thought) {
            abortTagSuggestions(thought.id, tagSuggestionMode(suggestionMode, hybrid), thought.tags || []);
        }

        // Reset state
        setHideAiSuggestions(false);
        setSuggestedTags([]);
        
        // Re-run the suggestions logic
        if (thought && thought.thought) {
            setSuggestingTags(true);

            const allTags = allTagsProp || getAllUniqueTags(thoughts);
            const existingTags = thought.tags || [];

            const apiCall = suggestionMode === 'existingTags'
                ? suggestTagsForThought(thought.thought, allTags, existingTags, { hybrid })
                : suggestNewThemes(thought.thought, allTags, existingTags);

            apiCall
                .then(suggestions => {
                    // Filter based on suggestion mode
                    if (suggestionMode === 'existingTags') {
                        suggestions = applyExistingTagsFilter(suggestions, allTags, hybrid);
                    } else {
                        // For newThemes mode, only keep tags that are NOT in the unique tags list
                        suggestions = suggestions.filter(tag => !allTags.includes(tag));
                    }
                    setSuggestedTags(suggestions);
                })
                .catch(error => {
                    console.error('Error getting refreshed suggestions:', error);
                })
                .finally(() => {
                    setSuggestingTags(false);
                });
        }
    };

    // Toggle between suggestion modes (existing tags or new themes)
    const toggleSuggestionMode = () => {
        // Clear existing suggestions
        setSuggestedTags([]);
        
        // Toggle the mode
        setSuggestionMode(prevMode => prevMode === 'existingTags' ? 'newThemes' : 'existingTags');
        
        // Clear any selected tags
        setSelectedAiTags([]);
    };

    // Function to directly update the UI without a state change
    const updateSliderUI = (value) => {
        // Update "Not set" button
        const notSetButton = document.querySelector('.importance-not-set');
        if (notSetButton) {
            notSetButton.style.fontWeight = value === null ? 'bold' : 'normal';
            notSetButton.style.color = colors.textSecondary;
        }
        
        // Update slider filled portion
        const sliderFill = document.querySelector('.importance-slider-fill');
        if (sliderFill && value !== null) {
            sliderFill.style.width = value === 1 ? '5%' : `${((value - 1) / 4) * 100}%`;
            sliderFill.style.display = 'block';
        } else if (sliderFill) {
            sliderFill.style.display = 'none';
        }
        
        // Update number buttons
        const numberButtons = document.querySelectorAll('.importance-number');
        if (numberButtons) {
            numberButtons.forEach((button, index) => {
                const level = index + 1;
                button.style.color = value === level ? (colors.accent.purple || '#4CAF50') : colors.textSecondary;
            });
        }
    };

    // Auto-save the importance value after a brief delay
    const handleImportanceChange = (value) => {
        // Store the value in ref without causing re-render
        importanceValueRef.current = value;
        
        // Directly update the UI elements
        updateSliderUI(value);
        
        // Clear any existing timeout
        if (saveTimeout) clearTimeout(saveTimeout);
        
        // Set a new timeout to save after 500ms of inactivity
        const timeout = setTimeout(async () => {
            if (!isSaving && value !== thought.importance) {
                setIsSaving(true);
                
                // Update saving indicator without re-rendering
                const savingIndicator = document.querySelector('.importance-saving');
                if (savingIndicator) {
                    savingIndicator.style.display = 'block';
                }
                
                let savedOk = false;
                try {
                    const updatedThought = { ...thought, importance: value };

                    // If updateThoughtLocally is available, use it for optimistic updates
                    if (updateThoughtLocally) {
                        // Optimistically update the local state first
                        updateThoughtLocally(updatedThought);

                        // Save without triggering a full data reload
                        await onSave(updatedThought, true); // Pass flag to avoid reload
                    } else {
                        // Fallback to regular save that might reload all data
                        await onSave(updatedThought);
                    }

                    // Update the local thought object directly with a minimal update
                    // This change prevents a full re-render cascade
                    setThought(prev => {
                        if (!prev) return updatedThought;
                        return Object.assign({}, prev, { importance: value });
                    });
                    savedOk = true;
                } catch (error) {
                    console.error('Error saving importance:', error);
                } finally {
                    setIsSaving(false);

                    // Hide saving indicator without re-rendering
                    if (savingIndicator) {
                        savingIndicator.style.display = 'none';
                    }
                }

                // Rating >=4 can make the BEFORE-UPDATE trigger auto-add this thought to
                // the deck (importance >= 4 AND in_deck IS NULL -> true). The save used
                // skipReload, so read in_deck back from the DB and sync the "Add to deck"
                // chip — the trigger is the source of truth, so a tossed card
                // (in_deck = false) is correctly left out. Runs AFTER the finally so it
                // doesn't extend the isSaving window (a rapid re-rating isn't dropped),
                // and only when the save succeeded. `targetId` guards against the thought
                // changing under us mid-refetch (e.g. navigating to another thought on
                // the same route): we only apply the result if it's still this thought.
                if (savedOk && value >= 4) {
                    const targetId = thought.id;
                    try {
                        const fresh = await getThoughtById(targetId);
                        if (fresh) {
                            setThought(prev => (prev && prev.id === targetId ? { ...prev, inDeck: fresh.inDeck } : prev));
                            if (updateThoughtLocally) {
                                const base = latestThoughtRef.current;
                                // Merge ONLY inDeck onto the latest committed thought so a
                                // concurrent field edit isn't clobbered; skip if the base
                                // is a different (or missing) thought.
                                if (base && base.id === targetId) {
                                    updateThoughtLocally({ ...base, importance: value, inDeck: fresh.inDeck });
                                }
                            }
                        }
                    } catch (syncErr) {
                        // Non-fatal: importance is saved; the chip will be correct on next load.
                        console.error('Error syncing deck membership after importance change:', syncErr);
                    }
                }
            }
        }, 500);

        setSaveTimeout(timeout);
    };

    // After the tag editor opens via the `t` shortcut, focus the input
    // on the next render so the user can start typing immediately.
    const [pendingFocusTagInput, setPendingFocusTagInput] = useState(false);
    useEffect(() => {
        if (!pendingFocusTagInput || !showTagsEditor) return;
        const input = document.querySelector('input[placeholder^="Type a tag"]');
        if (input) input.focus();
        setPendingFocusTagInput(false);
    }, [pendingFocusTagInput, showTagsEditor]);

    // Card-mode keyboard shortcuts: 0-5 importance, t opens tag editor.
    // Gated on no text field being focused so they don't fight typing.
    // Rebinds on every render so handlers close over the latest state.
    useEffect(() => {
        if (!cardMode) return undefined;
        const onKeyDown = (e) => {
            if (e.metaKey || e.ctrlKey || e.altKey) return;
            const ae = document.activeElement;
            if (ae && (ae.tagName === 'INPUT' || ae.tagName === 'TEXTAREA' || ae.isContentEditable)) return;
            if (e.key === '0') {
                e.preventDefault();
                handleImportanceChange(null);
            } else if (['1', '2', '3', '4', '5'].includes(e.key)) {
                e.preventDefault();
                handleImportanceChange(Number(e.key));
            } else if (e.key.toLowerCase() === 't') {
                e.preventDefault();
                handleStartTagEdit();
                setPendingFocusTagInput(true);
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    });

    const formattedDate = thought ? formatTimestamp(thought.timestamp) : '';

    // Deck membership toggle. Writes only `in_deck` via deckService — the
    // thought itself is untouched. Optimistic (updates `thought` + the parent
    // cache via updateThoughtLocally), reverts both on failure. Also the
    // un-toss path. deckTogglingRef is an in-flight lock so a rapid double-tap
    // can't race two writes to opposite states.
    const deckTogglingRef = useRef(false);
    const handleDeckToggle = () => {
        if (!thought || deckTogglingRef.current) return;
        const next = !(thought.inDeck === true);
        const optimistic = { ...thought, inDeck: next };
        deckTogglingRef.current = true;
        setThought(optimistic);
        if (updateThoughtLocally) updateThoughtLocally(optimistic);
        (async () => {
            try {
                if (next) await addToDeck(thought.id);
                else await tossCard(thought.id);
            } catch (e) {
                console.error('Error toggling deck membership:', e);
                // Revert ONLY in_deck, merged onto the LATEST thought — not a
                // snapshot captured at click time. Restoring a stale snapshot
                // would clobber any field edit (comments/importance/tags) the
                // user made while this write was in flight. latestThoughtRef
                // holds the most recent committed thought.
                const base = latestThoughtRef.current || optimistic;
                const reverted = { ...base, inDeck: !next };
                setThought(reverted);
                if (updateThoughtLocally) updateThoughtLocally(reverted);
            } finally {
                deckTogglingRef.current = false;
            }
        })();
    };

    const handleEdit = () => {
        // No-op in cardMode: the edit pen is hidden, but the body of the
        // card has onClick={handleEdit} too — without this gate, tapping
        // anywhere on the card text in review mode kicks the user out
        // to /edit/:id and loses their queue progress.
        if (cardMode) return;
        // Apply exit animation before navigation
        const detailContainer = document.querySelector('.page-content');
        if (detailContainer) {
            detailContainer.style.animation = 'fadeOut 0.2s cubic-bezier(0.2, 0, 0, 1) forwards';
            setTimeout(() => {
                navigate(`/edit/${thought.id}`);
            }, 180); // Slightly shorter than animation duration
        } else {
            navigate(`/edit/${thought.id}`);
        }
    };
    
    // Handle thought deletion
    const handleDeleteThought = () => {
        if (window.confirm("Are you sure you want to delete this thought? This action cannot be undone.")) {
            // Apply exit animation before navigation
            const detailContainer = document.querySelector('.page-content');
            if (detailContainer) {
                detailContainer.style.animation = 'fadeOut 0.2s cubic-bezier(0.2, 0, 0, 1) forwards';
                setTimeout(() => {
                    // Delete the thought
                    deleteThought(id);
                    // Navigate back to the main page
                    navigate('/', { replace: true });
                }, 180);
            } else {
                deleteThought(id);
                navigate('/', { replace: true });
            }
        }
    };

    // Toggle a tag in the selected AI tags list
    const toggleAiTagSelection = (tag) => {
        setSelectedAiTags(prev => {
            if (prev.includes(tag)) {
                return prev.filter(t => t !== tag);
            } else {
                return [...prev, tag];
            }
        });
    };
    
    // Clear all selected AI tags
    const clearSelectedAiTags = () => {
        setSelectedAiTags([]);
    };
    
    // Save selected AI tags
    const saveSelectedAiTags = async () => {
        if (selectedAiTags.length === 0) return;
        
        // Hide AI suggestions immediately before saving starts
        setHideAiSuggestions(true);
        setPermanentlyHideAiSuggestions(true);
        setIsSavingTags(true);
        
        try {
            // Prepare new tags list combining existing tags and newly selected AI tags
            const currentTags = thought.tags || [];
            const newTags = [...new Set([...currentTags, ...selectedAiTags])];
            
            // Create a new thought object but preserve the same reference for properties we don't change
            const updatedThought = { ...thought, tags: newTags };
            
            // If updateThoughtLocally is available, use it for optimistic updates
            if (updateThoughtLocally) {
                // Optimistically update the local state first
                updateThoughtLocally(updatedThought);
                
                // Save without triggering a full data reload
                await onSave(updatedThought, true); // Pass flag to avoid reload
            } else {
                // Fallback to regular save that might reload all data
                await onSave(updatedThought);
            }
            
            // Update only the tags property to avoid re-rendering everything
            setThought(prev => {
                if (!prev) return updatedThought;
                return { 
                    ...prev, 
                    tags: newTags 
                };
            });
            
            // Clear selected tags after saving
            setSelectedAiTags([]);
        } catch (error) {
            console.error('Error saving AI suggested tags:', error);
            // If there's an error, show the AI suggestions again
            setHideAiSuggestions(false);
        } finally {
            setIsSavingTags(false);
        }
    };

    // Handle starting tag edit
    const handleStartTagEdit = () => {
        tagsManager.setTags(thought.tags || []);
        setShowTagsEditor(true);
    };

    // Handle canceling tag edit
    const handleCancelTagEdit = () => {
        setShowTagsEditor(false);
        tagsManager.resetTags();
    };

    // Handle saving tag changes
    const handleSaveTagChanges = async () => {
        await handleTagsChange(tagsManager.selectedTags);
        setShowTagsEditor(false);
    };

    // Handle starting comments edit
    const handleStartCommentsEdit = () => {
        setCommentsValue(thought.comments || '');
        setShowCommentsEditor(true);
    };

    // Handle canceling comments edit
    const handleCancelCommentsEdit = () => {
        setShowCommentsEditor(false);
        setCommentsValue('');
    };

    // Handle saving comments changes
    const handleSaveCommentsChanges = async () => {
        if (isCommentsSaving) return;
        setIsCommentsSaving(true);
        
        try {
            const updatedThought = { ...thought, comments: commentsValue };
            
            // If updateThoughtLocally is available, use it for optimistic updates
            if (updateThoughtLocally) {
                // Optimistically update the local state first
                updateThoughtLocally(updatedThought);
                
                // Save without triggering a full data reload
                await onSave(updatedThought, true);
            } else {
                // Fallback to regular save that might reload all data
                await onSave(updatedThought);
            }
            
            // Update the local thought object
            setThought(prev => ({
                ...prev,
                comments: commentsValue
            }));
            
            setShowCommentsEditor(false);
        } catch (error) {
            console.error('Error saving comments:', error);
        } finally {
            setIsCommentsSaving(false);
        }
    };

    // Get button styles from ComposePage
    const buttonStyles = styles.composePage.desktop;

    if (isLoading) {
        return (
            <div style={{ 
                padding: '24px 8px', 
                textAlign: 'center',
                animation: 'fadeIn 0.3s cubic-bezier(0.2, 0, 0, 1)',
                opacity: 0, // Start invisible for the animation
            }} className="page-content">
                {/* Empty loading state with no message */}
                <style>
                    {`
                        @keyframes fadeIn {
                            from { opacity: 0; }
                            to { opacity: 1; }
                        }
                        
                        @keyframes fadeOut {
                            from { opacity: 1; }
                            to { opacity: 0; }
                        }
                    `}
                </style>
            </div>
        );
    }

    if (!thought) {
        return (
            <div style={{ 
                padding: '24px 8px', 
                textAlign: 'center',
                animation: 'fadeIn 0.3s cubic-bezier(0.2, 0, 0, 1)',
                opacity: 1,
            }} className="page-content">
                <style>
                    {`
                        @keyframes fadeIn {
                            from { opacity: 0; }
                            to { opacity: 1; }
                        }
                        
                        @keyframes fadeOut {
                            from { opacity: 1; }
                            to { opacity: 0; }
                        }
                    `}
                </style>
                <p>Thought not found</p>
                <button 
                    onClick={() => navigate('/')} 
                    style={{ ...styles.button, marginTop: '16px' }}
                >
                    Back to Library
                </button>
            </div>
        );
    }

    return (
        <div
            {...swipeHandlers}
            style={{
                padding: '8px 8px',
                maxWidth: '1000px',
                margin: '0 auto',
                opacity: isVisible ? 1 : 0,
                transition: 'opacity 0.3s cubic-bezier(0.2, 0, 0, 1)',
                willChange: 'opacity',
                // In cardMode the parent gives us a fixed-height slot; lay out
                // children as a flex column so the body region can flex-grow
                // to fill remaining space (after tags/AI/importance) and
                // scroll internally — the action bar below stays anchored.
                ...(cardMode ? {
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    minHeight: 0,
                    width: '100%',
                    boxSizing: 'border-box',
                } : null),
            }}
            className="page-content"
        >
            {/* Define animations */}
            <style>
                {`
                    @keyframes fadeIn {
                        from { opacity: 0; }
                        to { opacity: 1; }
                    }
                    
                    @keyframes fadeOut {
                        from { opacity: 1; }
                        to { opacity: 0; }
                    }
                `}
            </style>
            
            {/* Back button and date/edit in same row (hidden in cardMode) */}
            {!cardMode && <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '16px',
                paddingLeft: '0px',
            }}>
                {/* Back button (left side) */}
                <button 
                    onClick={handleBack} 
                    style={{
                        backgroundColor: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        padding: '8px',
                        marginLeft: '0px',
                    }}
                >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M15 18L9 12L15 6" stroke={colors.textPrimary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </button>
                
                {/* Date and edit on right side */}
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span style={{ fontSize: '14px', color: colors.textSecondary, marginRight: '8px' }}>{formattedDate}</span>
                    <button 
                        onClick={handleEdit}
                        style={{
                            backgroundColor: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '8px',
                            display: 'flex',
                            alignItems: 'center',
                        }}
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13" stroke={colors.textPrimary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M18.5 2.50001C18.8978 2.10219 19.4374 1.87869 20 1.87869C20.5626 1.87869 21.1022 2.10219 21.5 2.50001C21.8978 2.89784 22.1213 3.4374 22.1213 4.00001C22.1213 4.56262 21.8978 5.10219 21.5 5.50001L12 15L8 16L9 12L18.5 2.50001Z" stroke={colors.textPrimary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </button>
                </div>
            </div>}

            {/* Main thought content. In cardMode the body floats on the page
                background (no pill chrome) and flex-grows to fill the space
                above the classification card. */}
            <div
                onClick={handleEdit}
                style={{
                    cursor: cardMode ? 'default' : 'pointer',
                    marginBottom: cardMode ? 0 : '24px',
                    position: 'relative',
                    padding: cardMode ? '8px 4px' : '16px',
                    backgroundColor: cardMode ? 'transparent' : colors.surfaceElevated,
                    borderRadius: cardMode ? 0 : '12px',
                    ...(cardMode ? {
                        flex: 1,
                        minHeight: 0,
                        overflowY: 'auto',
                    } : null),
                }}
            >
                <p style={{
                    fontSize: '18px',
                    lineHeight: '1.6',
                    color: colors.textPrimary,
                    whiteSpace: 'pre-wrap',
                    marginTop: 0,
                    marginBottom: 0,
                    fontWeight: '400',
                    paddingRight: cardMode ? 0 : '32px',
                }}>
                    {thought.thought}
                </p>

                {/* Copy button — hidden in cardMode (chrome-free preview) */}
                {!cardMode && <button
                    onClick={(e) => {
                        e.stopPropagation(); // Prevent triggering the edit action
                        navigator.clipboard.writeText(thought.thought)
                            .then(() => {
                                // Show success checkmark
                                setShowCopyCheck(true);

                                // Reset back to copy icon after a moment
                                setTimeout(() => {
                                    setShowCopyCheck(false);
                                }, 500);
                            })
                            .catch(err => console.error('Failed to copy text: ', err));
                    }}
                    style={{
                        position: 'absolute',
                        top: '8px',
                        right: '8px',
                        backgroundColor: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        width: '36px',
                        height: '36px',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        opacity: 0.7,
                        transition: 'all 0.2s ease',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                    onMouseLeave={(e) => e.currentTarget.style.opacity = '0.7'}
                    title="Copy to clipboard"
                >
                    {showCopyCheck ? (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M5 12L10 17L20 7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    ) : (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <rect x="8" y="8" width="12" height="12" rx="2" stroke="white" strokeWidth="2" />
                            <path d="M16 8V6c0-1.10457-.8954-2-2-2H6c-1.10457 0-2 .89543-2 2v8c0 1.1046.89543 2 2 2h2" stroke="white" strokeWidth="2" />
                        </svg>
                    )}
                </button>}
            </div>

            {/* Metadata section. In cardMode this is the only rounded surface
                — body lives outside it on the page background — so the panel
                hugs the classification controls regardless of body length. */}
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '20px',
                paddingTop: cardMode ? '16px' : '0px',
                ...(cardMode ? {
                    flexShrink: 0,
                    background: colors.surfaceElevated,
                    border: `1px solid ${colors.divider}`,
                    borderRadius: 18,
                    padding: '16px 8px',
                } : null),
            }}>
                {/* Tags */}
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px',
                    margin: '5px 0',
                    padding: '0 8px',
                }}>
                    <label style={{ 
                        display: 'block', 
                        fontSize: '14px',
                        color: colors.textSecondary,
                        marginBottom: '4px',
                    }}>
                        Tags
                    </label>
                    {showTagsEditor ? (
                        <div>
                            <TagsInput
                                tagInput={tagsManager.tagInput}
                                selectedTags={tagsManager.selectedTags}
                                filteredTags={tagsManager.filteredTags}
                                showDropdown={tagsManager.showTagDropdown}
                                onInputChange={tagsManager.handleTagInputChange}
                                onKeyPress={tagsManager.handleTagKeyPress}
                                onFocus={() => tagsManager.setShowTagDropdown(true)}
                                onBlur={(e) => {
                                    // Delay closing to check if click was on a dropdown item
                                    const currentTarget = e.currentTarget;
                                    setTimeout(() => {
                                        // Only close if focus is not within the input or dropdown
                                        if (!currentTarget.contains(document.activeElement)) {
                                            tagsManager.setShowTagDropdown(false);
                                        }
                                    }, 300);
                                }}
                                onTagSelect={(tag) => {
                                    // Compute the new array inline because addTag's setState
                                    // is async and tagsManager.selectedTags would be stale.
                                    const next = tagsManager.selectedTags.includes(tag)
                                        ? tagsManager.selectedTags
                                        : [...tagsManager.selectedTags, tag];
                                    tagsManager.addTag(tag);
                                    if (cardMode) {
                                        // Auto-save the pick so the user doesn't have to click
                                        // Save — but keep the editor open so they can keep
                                        // picking. Esc / Cmd+S / Save closes when they're done.
                                        handleTagsChange(next);
                                    }
                                    tagsManager.setShowTagDropdown(true);
                                }}
                                onClose={handleSaveTagChanges}
                                onTagRemove={tagsManager.removeTag}
                            />
                            {/* Save/Cancel buttons */}
                            <div style={{
                                display: 'flex',
                                gap: '8px',
                                marginTop: '16px',
                                justifyContent: 'flex-end'
                            }}>
                                <button
                                    onClick={handleCancelTagEdit}
                                    style={{
                                        ...buttonStyles.addButton,
                                        cursor: 'pointer'
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleSaveTagChanges}
                                    style={{
                                        ...buttonStyles.saveButton,
                                        cursor: 'pointer'
                                    }}
                                >
                                    Save
                                </button>
                            </div>
                        </div>
                    ) : (
                        thought.tags && thought.tags.length > 0 ? (
                            <div 
                                onClick={handleStartTagEdit}
                                style={{ 
                                    display: 'flex', 
                                    flexWrap: 'wrap', 
                                    gap: '8px',
                                    cursor: 'pointer'
                                }}
                            >
                                {thought.tags.map((tag, i) => (
                                    <span key={i} style={{
                                        ...styles.tagPill,
                                        backgroundColor: colors.surface,
                                        color: colors.accent.white,
                                        boxShadow: 'none',
                                        border: `1px solid ${colors.accent.white}20`,
                                        fontWeight: 'bold',
                                        fontSize: '14px',
                                    }}>
                                        {tag}
                                    </span>
                                ))}
                            </div>
                        ) : (
                            <div style={{ 
                                display: 'flex',
                                alignItems: 'center'
                            }}>
                                <ActionButton
                                    onClick={handleStartTagEdit}
                                    icon={<PlusIcon width={14} height={14} />}
                                    text="No tags"
                                    color={colors.textSecondary}
                                    fontSize={17}
                                />
                            </div>
                        )
                    )}
                </div>

                {/* AI Suggestions Magic Wand - show when suggestions are permanently hidden */}
                {(permanentlyHideAiSuggestions || !suggestedTags || suggestedTags.length === 0) && !suggestingTags && (
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        margin: '5px 0',
                        padding: '0 8px',
                    }}>
                        <button
                            onClick={() => {
                                setPermanentlyHideAiSuggestions(false);
                                setHideAiSuggestions(false);
                                setSelectedAiTags([]); // Clear selected tags immediately
                                // Refresh suggestions when showing them again
                                refreshAiSuggestions();
                            }}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                padding: '8px 12px',
                                backgroundColor: `${colors.accent.purple}10`,
                                border: 'none',
                                borderRadius: '8px',
                                fontSize: '14px',
                                color: colors.accent.purple,
                                cursor: 'pointer',
                                transition: 'all 150ms ease',
                                fontWeight: 'bold',
                            }}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill={colors.accent.purple}>
                                <path d="m195-143-51-51 292-292-172-42 186-116-16-219 168 141 202-82-82 203 142 167-218.97-16L529-263l-42-172-292 292Zm21-481-72-72 72-72 72 72-72 72Zm341 177 49-78 92 7-59.31-70.2 34.6-85.8-85.02 35L518-698l7 92-79 48.39 89.35 21.73L557-447Zm139 303-72-72 72-72 72 72-72 72ZM572-573Z"/>
                            </svg>
                            Suggest Tags
                        </button>
                    </div>
                )}

                {/* AI Suggested Tags */}
                {suggestedTags && suggestedTags.length > 0 && !hideAiSuggestions && !permanentlyHideAiSuggestions && (
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '4px',
                        margin: '5px 0',
                        padding: '0 8px',
                    }}>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '4px',
                        }}>
                            <label style={{ 
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                fontSize: '14px',
                                color: colors.textSecondary,
                            }}>
                                {/* Switch between icons based on the mode */}
                                {suggestionMode === 'existingTags' ? (
                                    <svg width="32" height="32" viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg" style={{ position: 'relative', top: '3px' }}>
                                        <path d="M20 16C11 16 11 32 20 32H48L40 24L48 32L40 40" 
                                            fill="none" 
                                            stroke={colors.accent.purple}
                                            strokeWidth="4" 
                                            strokeLinecap="round" 
                                            strokeLinejoin="round"/>
                                    </svg>
                                ) : (
                                    <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M9 16H15" stroke={colors.accent.purple} strokeWidth="2" strokeLinecap="round"/>
                                                <path d="M10 19H14" stroke={colors.accent.purple} strokeWidth="2" strokeLinecap="round"/>
                                                <path d="M12 3C8.686 3 6 5.686 6 9C6 10.933 7.025 12.59 8.5 13.535C9.109 13.923 9.5 14.58 9.5 15.293V16H14.5V15.293C14.5 14.58 14.891 13.923 15.5 13.535C16.975 12.59 18 10.933 18 9C18 5.686 15.314 3 12 3Z" 
                                            stroke={colors.accent.purple} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                )}
                                
                                {suggestionMode === 'existingTags' ? 'Suggested' : 'Ideas'}
                                
                                {/* Mode toggle buttons */}
                                <div style={{ display: 'flex', marginLeft: '8px' }}>
                                    {/* Lightbulb/Tags icon (toggle to themes/tags) */}
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            toggleSuggestionMode();
                                        }}
                                        style={{
                                            background: 'transparent',
                                            border: 'none',
                                            cursor: 'pointer',
                                            padding: '4px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}
                                        title={suggestionMode === 'existingTags' ? "Switch to theme suggestions" : "Switch to tag suggestions"}
                                    >
                                        {/* Show the opposite icon of current mode to indicate what we'll switch to */}
                                        {suggestionMode === 'existingTags' ? (
                                            <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M9 16H15" stroke={colors.textSecondary} strokeWidth="2" strokeLinecap="round"/>
                                                <path d="M10 19H14" stroke={colors.textSecondary} strokeWidth="2" strokeLinecap="round"/>                                                
                                                <path d="M12 3C8.686 3 6 5.686 6 9C6 10.933 7.025 12.59 8.5 13.535C9.109 13.923 9.5 14.58 9.5 15.293V16H14.5V15.293C14.5 14.58 14.891 13.923 15.5 13.535C16.975 12.59 18 10.933 18 9C18 5.686 15.314 3 12 3Z" 
                                                    stroke={colors.textSecondary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                        ) : (
                                            <svg width="32" height="32" viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg"
                                                style={{ position: 'relative', top: '3px' }}>
                                                <path d="M20 16C11 16 11 32 20 32H48L40 24L48 32L40 40" 
                                                    fill="none" 
                                                    stroke={colors.textSecondary}
                                                    strokeWidth="4" 
                                                    strokeLinecap="round" 
                                                    strokeLinejoin="round"/>
                                            </svg>
                                        )}
                                    </button>
                                    
                                    {/* Refresh button */}
                                    {!suggestingTags && suggestedTags.length > 0 && (
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation(); // Prevent any parent click handlers
                                                refreshAiSuggestions();
                                            }}
                                            style={{
                                                background: 'transparent',
                                                border: 'none',
                                                cursor: 'pointer',
                                                padding: '4px',
                                                marginLeft: '4px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                            title="Refresh suggestions"
                                        >
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M19.5 8.5C17.8 6.2 15.1 4.5 12 4.5C7.9 4.5 4.5 7.9 4.5 12C4.5 16.1 7.9 19.5 12 19.5C15.1 19.5 17.8 17.8 19.5 15.5" stroke={colors.textSecondary} strokeWidth="2" strokeLinecap="round"/>
                                                <path d="M15 8.5H19.5V4" stroke={colors.textSecondary} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                        </button>
                                    )}
                                </div>
                            </label>
                            
                            {/* Action buttons for multi-select */}
                            {selectedAiTags.length > 0 && (
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    {/* Cancel button (X) */}
                                    <button
                                        onClick={clearSelectedAiTags}
                                        style={{
                                            background: 'transparent',
                                            border: 'none',
                                            cursor: 'pointer',
                                            padding: '4px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}
                                        title="Clear selection"
                                    >
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M18 6L6 18" stroke="#FF5A5A" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                                            <path d="M6 6L18 18" stroke="#FF5A5A" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    </button>
                                    
                                    {/* Add tags button (✓) */}
                                    <button
                                        onClick={saveSelectedAiTags}
                                        disabled={isSavingTags}
                                        style={{
                                            background: 'transparent',
                                            border: 'none',
                                            cursor: 'pointer',
                                            padding: '4px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            opacity: isSavingTags ? 0.5 : 1,
                                        }}
                                        title="Add selected tags"
                                    >
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M5 12L10 17L20 7" stroke={colors.accent.white} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    </button>
                                </div>
                            )}
                        </div>
                        
                        <div style={{ 
                            display: 'flex', 
                            flexWrap: 'wrap', 
                            gap: '8px',
                            padding: '4px 0'
                        }}>
                            {suggestedTags
                                .filter(tag => !thought.tags || !thought.tags.includes(tag))
                                .map((tag, i) => (
                                    <span 
                                        key={i} 
                                        onClick={() => toggleAiTagSelection(tag)}
                                        style={{
                                            backgroundColor: selectedAiTags.includes(tag) 
                                                ? `${colors.accent.purple}30` 
                                                : `${colors.accent.purple}10`,
                                            color: colors.accent.purple,
                                            // Border is ALWAYS 1px (transparent when unselected) so
                                            // selecting only changes its color — never the box size.
                                            // Toggling border none↔1px would grow this auto-width chip
                                            // by 2px, and `transition: all` would animate that reflow
                                            // (the chip visibly swelled then settled). Transition only
                                            // colors so layout can never animate.
                                            border: `1px solid ${selectedAiTags.includes(tag) ? colors.accent.purple : 'transparent'}`,
                                            borderRadius: '16px',
                                            padding: '8px 14px',
                                            fontSize: '14px',
                                            fontWeight: '500',
                                            whiteSpace: 'nowrap',
                                            transition: 'background-color 0.2s cubic-bezier(0.2, 0, 0, 1), border-color 0.2s cubic-bezier(0.2, 0, 0, 1)',
                                            cursor: 'pointer',
                                        }}
                                    >
                                        {tag}
                                    </span>
                                ))}
                        </div>
                    
                    </div>
                )}

                {/* Appears In section - Collections (hidden in cardMode) */}
                {!cardMode && <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px',
                    margin: '5px 0',
                    padding: '0 8px',
                }}>
                    <label style={{
                        display: 'block',
                        fontSize: '14px',
                        color: colors.textSecondary,
                        marginBottom: '4px',
                    }}>
                        Appears In
                    </label>
                    {collectionsLoading ? (
                        <div style={{ 
                            color: colors.textSecondary,
                            fontSize: '14px',
                            padding: '4px 0'
                        }}>
                        </div>
                    ) : showCollectionsManager ? (
                        <div style={{ 
                            display: 'flex', 
                            flexDirection: 'column',
                            gap: '16px'
                        }}>
                            {/* Search input */}
                            <input
                                type="text"
                                value={collectionSearchTerm}
                                onChange={(e) => setCollectionSearchTerm(e.target.value)}
                                placeholder="Search collections..."
                                style={{
                                    width: '100%',
                                    padding: '8px 12px',
                                    backgroundColor: colors.surfaceElevated,
                                    border: 'none',
                                    borderRadius: '8px',
                                    color: colors.textPrimary,
                                    fontSize: '14px',
                                }}
                            />
                            
                            {/* Collections list */}
                            <div style={{ 
                                display: 'flex', 
                                flexDirection: 'column',
                                gap: '0px',
                                maxHeight: '300px',
                                overflowY: 'auto',
                            }}>
                                {allCollections
                                    .filter(collection => 
                                        collection.name.toLowerCase().includes(collectionSearchTerm.toLowerCase())
                                    )
                                    .sort((a, b) => a.name.localeCompare(b.name)) // Sort alphabetically
                                    .map((collection, index) => (
                                        <div 
                                            key={collection.id}
                                            onClick={() => toggleCollectionSelection(collection.id)}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '12px',
                                                padding: '16px',
                                                backgroundColor: colors.background,
                                                cursor: 'pointer',
                                                borderBottom: index === allCollections.filter(c => 
                                                    c.name.toLowerCase().includes(collectionSearchTerm.toLowerCase())
                                                ).length - 1 ? 'none' : `1px solid ${colors.divider}`,
                                            }}
                                        >
                                            {/* Checkmark moved to left */}
                                            <div style={{
                                                width: '20px',
                                                height: '20px',
                                                borderRadius: '10px',
                                                backgroundColor: selectedCollectionIds.includes(collection.id) 
                                                    ? colors.accent.white 
                                                    : 'transparent',
                                                border: selectedCollectionIds.includes(collection.id) 
                                                    ? 'none' 
                                                    : `1px solid ${colors.divider}`,
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                                flexShrink: 0,
                                            }}>
                                                {selectedCollectionIds.includes(collection.id) && (
                                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <path d="M20 6L9 17L4 12" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                    </svg>
                                                )}
                                            </div>
                                            
                                            <span style={{
                                                fontSize: '15px',
                                                lineHeight: '1.5',
                                                color: colors.textPrimary,
                                            }}>
                                                {collection.name}
                                            </span>
                                        </div>
                                    ))}
                            </div>
                            
                            {/* Action buttons */}
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginTop: '8px',
                            }}>
                                <button
                                    onClick={() => setShowCreateCollectionModal(true)}
                                    style={{
                                        backgroundColor: 'transparent',
                                        border: 'none',
                                        color: colors.textSecondary,
                                        padding: '8px',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px',
                                    }}
                                >
                                    <PlusIcon width={14} height={14} />
                                    <span>New Collection</span>
                                </button>
                                
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    <button
                                        onClick={() => setShowCollectionsManager(false)}
                                        style={{
                                            backgroundColor: 'transparent',
                                            color: colors.accent.white,
                                            border: 'none',
                                            borderRadius: '20px',
                                            padding: '8px 16px',
                                            fontWeight: 'bold',
                                            fontSize: '12px',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        Cancel
                                    </button>
                                    
                                    <button
                                        onClick={handleSaveCollections}
                                        disabled={isCollectionsSaving}
                                        style={{
                                            backgroundColor: colors.accent.white,
                                            color: 'black',
                                            border: 'none',
                                            borderRadius: '20px',
                                            padding: '8px 16px',
                                            fontWeight: 'bold',
                                            fontSize: '12px',
                                            cursor: !isCollectionsSaving ? 'pointer' : 'not-allowed',
                                            opacity: !isCollectionsSaving ? 1 : 0.5
                                        }}
                                    >
                                        {isCollectionsSaving ? 'Saving...' : 'Save'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    ) : collections && collections.length > 0 ? (
                        <div 
                            onClick={() => {
                                fetchAllCollections();
                                setShowCollectionsManager(true);
                            }}
                            style={{ 
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: '8px',
                                cursor: 'pointer'
                            }}
                        >
                            {collections
                                .filter(collection => !collection.name.startsWith('/')) // Filter out tag collections
                                .sort((a, b) => a.name.localeCompare(b.name)) // Sort alphabetically
                                .map((collection, i) => (
                                    <span key={i} style={{
                                        ...styles.tagPill,
                                        backgroundColor: colors.surface,
                                        color: colors.accent.purple,
                                        boxShadow: 'none',
                                        border: `1px solid ${colors.accent.purple}20`,
                                        fontWeight: 'bold',
                                        fontSize: '14px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px'
                                    }}>
                                        <Identicon seed={collection.id} size={16} marginRight={0} />
                                        {collection.name}
                                    </span>
                                ))}
                        </div>
                    ) : (
                        <div style={{ 
                            display: 'flex',
                            alignItems: 'center'
                        }}>
                            <ActionButton
                                onClick={() => {
                                    fetchAllCollections();
                                    setShowCollectionsManager(true);
                                }}
                                icon={<PlusIcon width={14} height={14} />}
                                text="No collections"
                                color={colors.textSecondary}
                                fontSize={17}
                            />
                        </div>
                    )}
                </div>}

                {/* Deck membership — flashcard deck toggle (also the un-toss path) */}
                {thought && (
                    <div style={{ margin: '5px 0', padding: '0 8px' }}>
                        <button
                            onClick={handleDeckToggle}
                            style={{
                                background: thought.inDeck === true ? colors.surface : 'transparent',
                                border: `1px solid ${colors.divider}`,
                                borderRadius: '8px',
                                padding: '6px 12px',
                                color: thought.inDeck === true ? colors.textPrimary : colors.textSecondary,
                                fontSize: '13px',
                                fontWeight: 600,
                                cursor: 'pointer',
                                WebkitTapHighlightColor: 'rgba(0,0,0,0)',
                            }}
                            title={thought.inDeck === true ? 'Remove from deck' : 'Add to deck'}
                        >
                            {thought.inDeck === true ? '✓ Added to deck' : 'Add to deck'}
                        </button>
                    </div>
                )}

                {/* Importance - Simple clean slider (now last) - using direct DOM manipulation */}
                <div style={{
                    margin: '5px 0',
                    padding: '0 8px',
                }}>
                    <div style={{ 
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '8px'
                    }}>
                        <label style={{ 
                            fontSize: '14px',
                            color: colors.textSecondary,
                            fontWeight: 'bold',
                        }}>
                            Importance
                        </label>
                        <span 
                            className="importance-saving"
                            style={{ 
                                fontSize: '14px',
                                color: colors.textSecondary,
                                opacity: 0.7,
                                fontWeight: 'bold',
                                display: isSaving ? 'block' : 'none',
                            }}
                        >
                            Saving...
                        </span>
                    </div>
                    
                    {/* "Not set" button */}
                    <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center' }}>
                        <button
                            className="importance-not-set"
                            type="button"
                            onClick={() => handleImportanceChange(null)}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                padding: '6px 10px',
                                backgroundColor: 'transparent',
                                border: 'none',
                                fontSize: '15px',
                                color: colors.textSecondary,
                                cursor: 'pointer',
                                transition: 'all 150ms ease',
                                fontWeight: thought.importance === null ? 'bold' : 'normal',
                            }}
                        >
                            Not set
                        </button>
                    </div>
                    
                    {/* Clean simple slider - without +/- signs */}
                    <div data-no-swipe="true" style={{
                        margin: '12px 0'
                    }}>
                        {/* Simple slider bar */}
                        <div role="slider" aria-label="Importance" data-no-swipe="true" style={{
                            position: 'relative',
                            height: '4px',
                            width: '100%',
                            backgroundColor: colors.divider,
                            borderRadius: '2px',
                            overflow: 'hidden',
                            cursor: 'pointer'
                        }}
                            onClick={(e) => {
                                // Calculate position as percentage of width
                                const rect = e.currentTarget.getBoundingClientRect();
                                const x = e.clientX - rect.left;
                                const percent = x / rect.width;
                                
                                // Convert to scale 1-5
                                const rawValue = 1 + Math.floor(percent * 5);
                                const value = Math.max(1, Math.min(5, rawValue));
                                
                                // Update importance
                                handleImportanceChange(value);
                            }}
                        >
                            {/* Filled portion */}
                            <div 
                                className="importance-slider-fill"
                                style={{
                                    position: 'absolute',
                                    left: 0,
                                    top: 0,
                                    bottom: 0,
                                    width: thought.importance === 1 ? '5%' : `${((thought.importance - 1) / 4) * 100}%`,
                                    backgroundColor: colors.accent.purple || '#4CAF50',
                                    transition: 'width 0.2s ease',
                                    display: thought.importance !== null ? 'block' : 'none'
                                }} 
                            />
                        </div>
                    </div>
                    
                    {/* Slider numbers */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        position: 'relative',
                        marginTop: '8px'
                    }}>
                        {[1, 2, 3, 4, 5].map(level => (
                            <button
                                key={level}
                                className="importance-number"
                                onClick={() => handleImportanceChange(level)}
                                style={{
                                    backgroundColor: 'transparent',
                                    border: 'none',
                                    padding: '4px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    color: thought.importance === level ? (colors.accent.purple || '#4CAF50') : colors.textSecondary,
                                }}
                            >
                                {level}
                            </button>
                        ))}
                    </div>
                </div>
                
                {/* Comments section (hidden in cardMode) */}
                {!cardMode && <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px',
                    margin: '5px 0',
                    padding: '0 8px',
                }}>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <label style={{ 
                            display: 'block', 
                            fontSize: '14px',
                            color: colors.textSecondary,
                            marginBottom: '4px',
                            fontWeight: 'bold',
                        }}>
                            Comments
                        </label>
                        
                        {thought.comments && !showCommentsEditor && (
                            <button 
                                onClick={handleStartCommentsEdit}
                                style={{
                                    backgroundColor: 'transparent',
                                    border: 'none',
                                    cursor: 'pointer',
                                    padding: '4px',
                                    display: 'flex',
                                    alignItems: 'center',
                                }}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13" stroke={colors.textSecondary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    <path d="M18.5 2.50001C18.8978 2.10219 19.4374 1.87869 20 1.87869C20.5626 1.87869 21.1022 2.10219 21.5 2.50001C21.8978 2.89784 22.1213 3.4374 22.1213 4.00001C22.1213 4.56262 21.8978 5.10219 21.5 5.50001L12 15L8 16L9 12L18.5 2.50001Z" stroke={colors.textSecondary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </button>
                        )}
                    </div>
                    
                    {showCommentsEditor ? (
                        <div>
                            <div style={{
                                backgroundColor: colors.surfaceElevated,
                                borderRadius: '12px',
                                marginBottom: '16px',
                                padding: '12px',
                            }}>
                                <textarea
                                    value={commentsValue}
                                    onChange={(e) => setCommentsValue(e.target.value)}
                                    placeholder="Add your comments here..."
                                    style={{
                                        width: '100%',
                                        minHeight: '120px',
                                        backgroundColor: 'transparent',
                                        border: 'none',
                                        color: colors.textPrimary,
                                        fontSize: '15px',
                                        lineHeight: '1.4',
                                        resize: 'vertical',
                                        outline: 'none',
                                        fontFamily: 'inherit',
                                    }}
                                    autoFocus
                                />
                            </div>
                            
                            {/* Save/Cancel buttons */}
                            <div style={{
                                display: 'flex',
                                gap: '8px',
                                justifyContent: 'flex-end'
                            }}>
                                <button
                                    onClick={handleCancelCommentsEdit}
                                    style={{
                                        ...buttonStyles.addButton,
                                        cursor: 'pointer'
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleSaveCommentsChanges}
                                    disabled={isCommentsSaving}
                                    style={{
                                        ...buttonStyles.saveButton,
                                        cursor: isCommentsSaving ? 'default' : 'pointer',
                                        opacity: isCommentsSaving ? 0.7 : 1,
                                    }}
                                >
                                    {isCommentsSaving ? 'Saving...' : 'Save'}
                                </button>
                            </div>
                        </div>
                    ) : thought.comments ? (
                        <div 
                            onClick={handleStartCommentsEdit}
                            style={{ 
                                cursor: 'pointer',
                                padding: '12px',
                                backgroundColor: colors.surfaceElevated,
                                borderRadius: '12px',
                                fontSize: '15px',
                                color: colors.textPrimary,
                                lineHeight: '1.4',
                                whiteSpace: 'pre-wrap',
                                position: 'relative',
                            }}
                        >
                            <p style={{
                                margin: 0,
                                paddingRight: '32px',
                                whiteSpace: 'pre-wrap',
                            }}>
                                {thought.comments}
                            </p>
                            
                            {/* Copy button for comments */}
                            <button
                                onClick={(e) => {
                                    e.stopPropagation(); // Prevent triggering the edit action
                                    navigator.clipboard.writeText(thought.comments)
                                        .then(() => {
                                            // Show success checkmark
                                            setShowCommentCopyCheck(true);
                                            
                                            // Reset back to copy icon after a moment
                                            setTimeout(() => {
                                                setShowCommentCopyCheck(false);
                                            }, 500);
                                        })
                                        .catch(err => console.error('Failed to copy comment text: ', err));
                                }}
                                style={{
                                    position: 'absolute',
                                    top: '8px',
                                    right: '8px',
                                    backgroundColor: 'transparent',
                                    border: 'none',
                                    cursor: 'pointer',
                                    width: '32px',
                                    height: '32px',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    opacity: 0.7,
                                    transition: 'all 0.2s ease',
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                                onMouseLeave={(e) => e.currentTarget.style.opacity = '0.7'}
                                title="Copy comment to clipboard"
                            >
                                {showCommentCopyCheck ? (
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M5 12L10 17L20 7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                ) : (
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <rect x="8" y="8" width="12" height="12" rx="2" stroke="white" strokeWidth="2" />
                                        <path d="M16 8V6c0-1.10457-.8954-2-2-2H6c-1.10457 0-2 .89543-2 2v8c0 1.1046.89543 2 2 2h2" stroke="white" strokeWidth="2" />
                                    </svg>
                                )}
                            </button>
                        </div>
                    ) : (
                        <div style={{
                            padding: '12px',
                            backgroundColor: `${colors.surfaceElevated}80`,
                            borderRadius: '12px',
                        }}>
                            <ActionButton
                                onClick={handleStartCommentsEdit}
                                icon={<PlusIcon width={14} height={14} />}
                                text="Add comments"
                                color={colors.textSecondary}
                                fontWeight="bold"
                                fontSize={14}
                            />
                        </div>
                    )}
                </div>}
            </div>

            {/* Delete Button (hidden in cardMode) */}
            {!cardMode && <div style={{
                display: 'flex',
                justifyContent: 'center',
                padding: '40px 0 40px 0',
                marginTop: '70px'
            }}>
                <button
                    onClick={handleDeleteThought}
                    style={{
                        backgroundColor: 'transparent',
                        color: 'rgb(230, 80, 71)',
                        border: 'none',
                        padding: '8px 16px',
                        fontSize: '15px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                    }}
                    onMouseEnter={(e) => e.target.style.opacity = '0.7'}
                    onMouseLeave={(e) => e.target.style.opacity = '1'}
                >
                    Delete thought
                </button>
            </div>}

            {/* Create Collection Modal */}
            <CreateCollectionModal 
                isVisible={showCreateCollectionModal}
                onClose={() => setShowCreateCollectionModal(false)}
                onSuccess={handleCollectionCreated}
            />
        </div>
    );
};

export default ThoughtDetail; 